import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useMobileOptimization } from "@/hooks/collaboration/useMobileOptimization";
import { Component, Viewport, Position, Size } from "@/types";
import { hapticFeedbackService } from "@/services/mobile/HapticFeedbackService";

interface ViewportBreakpoints {
  mobile: number;
  tablet: number;
  desktop: number;
  ultrawide: number;
}

interface ResponsiveProperties {
  mobile?: Partial<Component>;
  tablet?: Partial<Component>;
  desktop?: Partial<Component>;
  ultrawide?: Partial<Component>;
}

interface AdaptationRules {
  touchTargetMinimum: number;
  fontSizeScaling: number;
  spacingScaling: number;
  iconSizeScaling: number;
  buttonHeightMinimum: number;
  containerPaddingScaling: number;
  enableTextWrapping: boolean;
  enableStackedLayouts: boolean;
  enableCollapsibleSections: boolean;
  prioritizeVerticalSpace: boolean;
}

interface ViewportInfo {
  width: number;
  height: number;
  orientation: "portrait" | "landscape";
  devicePixelRatio: number;
  viewport: Viewport;
  isTouch: boolean;
  hasHover: boolean;
  hasKeyboard: boolean;
  safeArea: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
}

interface ResponsiveAdaptationContextValue {
  viewportInfo: ViewportInfo;
  adaptationRules: AdaptationRules;
  breakpoints: ViewportBreakpoints;
  adaptComponent: (component: Component) => Component;
  getResponsiveProperty: <T>(
    mobile: T,
    tablet?: T,
    desktop?: T,
    ultrawide?: T,
  ) => T;
  updateAdaptationRules: (rules: Partial<AdaptationRules>) => void;
  performanceMode: boolean;
  batterySaverMode: boolean;
}

const ResponsiveAdaptationContext =
  createContext<ResponsiveAdaptationContextValue | null>(null);

const fallbackBreakpoints: ViewportBreakpoints = {
  mobile: 768,
  tablet: 1024,
  desktop: 1920,
  ultrawide: 2560,
};

const fallbackAdaptationRules: AdaptationRules = {
  touchTargetMinimum: 44,
  fontSizeScaling: 1.2,
  spacingScaling: 1.15,
  iconSizeScaling: 1.1,
  buttonHeightMinimum: 44,
  containerPaddingScaling: 1.25,
  enableTextWrapping: true,
  enableStackedLayouts: true,
  enableCollapsibleSections: true,
  prioritizeVerticalSpace: true,
};

const toFiniteNumber = (value: number | string): number | undefined => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const ensureMinimumDimension = (
  value: number | string,
  minimum: number,
): number | string => {
  const numericValue = toFiniteNumber(value);
  return numericValue !== undefined && numericValue < minimum ? minimum : value;
};

interface ResponsiveAdaptationFrameworkProps {
  children: React.ReactNode;
  customBreakpoints?: Partial<ViewportBreakpoints>;
  customRules?: Partial<AdaptationRules>;
  enableAdvancedAdaptation?: boolean;
  enablePerformanceMode?: boolean;
}

/**
 * Responsive Adaptation Framework for Mobile-First Design
 * Provides viewport-aware component rendering with mobile-specific optimizations
 */
export const ResponsiveAdaptationFramework: React.FC<
  ResponsiveAdaptationFrameworkProps
> = ({
  children,
  customBreakpoints = {},
  customRules = {},
  enableAdvancedAdaptation = true,
  enablePerformanceMode = true,
}) => {
  const { viewport: editorViewport } = useEditorStore();
  const { isMobile, isLowPower, batteryLevel } = useMobileOptimization();

  // Default breakpoints following mobile-first approach
  // Aligned with standard responsive breakpoints for better compatibility with template JSON
  const defaultBreakpoints: ViewportBreakpoints = {
    mobile: 768,   // 0-768px = mobile
    tablet: 1024,  // 769-1024px = tablet
    desktop: 1920, // 1025-1920px = desktop
    ultrawide: 2560, // 1921px+ = ultrawide
  };

  const breakpoints: ViewportBreakpoints = {
    ...defaultBreakpoints,
    ...customBreakpoints,
  };

  // Default adaptation rules optimized for mobile
  const defaultAdaptationRules: AdaptationRules = {
    touchTargetMinimum: 44, // iOS/Android guidelines
    fontSizeScaling: 1.2, // Increase font size on mobile
    spacingScaling: 1.15, // Increase spacing for touch
    iconSizeScaling: 1.1, // Slightly larger icons
    buttonHeightMinimum: 44, // Minimum button height
    containerPaddingScaling: 1.25, // More padding on mobile
    enableTextWrapping: true,
    enableStackedLayouts: true,
    enableCollapsibleSections: true,
    prioritizeVerticalSpace: true,
  };

  const [adaptationRules, setAdaptationRules] = useState<AdaptationRules>({
    ...defaultAdaptationRules,
    ...customRules,
  });

  const [viewportInfo, setViewportInfo] = useState<ViewportInfo>(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const calculatedViewport =
        width <= breakpoints.mobile
          ? Viewport.MOBILE
          : width <= breakpoints.tablet
            ? Viewport.TABLET
            : Viewport.DESKTOP;

    return {
      width,
      height,
      orientation: width > height ? "landscape" : "portrait",
      devicePixelRatio: window.devicePixelRatio || 1,
      viewport: calculatedViewport,
      isTouch: "ontouchstart" in window || navigator.maxTouchPoints > 0,
      hasHover: window.matchMedia("(hover: hover)").matches,
      hasKeyboard: !("ontouchstart" in window),
      safeArea: { top: 0, bottom: 0, left: 0, right: 0 },
    };
  });

  // Performance monitoring
  const performanceMode = useMemo(
    () => enablePerformanceMode && (isLowPower || batteryLevel < 0.3),
    [enablePerformanceMode, isLowPower, batteryLevel],
  );

  const batterySaverMode = useMemo(() => batteryLevel < 0.2, [batteryLevel]);

  // Refs for performance optimization
  const resizeObserver = useRef<ResizeObserver | null>(null);
  const orientationTimeout = useRef<NodeJS.Timeout | null>(null);

  // Update viewport info on resize/orientation change
  const updateViewportInfo = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const orientation = width > height ? "landscape" : "portrait";

    const newViewport =
      width <= breakpoints.mobile
        ? Viewport.MOBILE
        : width <= breakpoints.tablet
          ? Viewport.TABLET
          : width <= breakpoints.desktop
            ? Viewport.DESKTOP
            : Viewport.DESKTOP; // Ultrawide falls back to desktop

    // Get safe area insets if available
    const safeArea = {
      top: parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--safe-area-inset-top",
        ) || "0",
      ),
      bottom: parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--safe-area-inset-bottom",
        ) || "0",
      ),
      left: parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--safe-area-inset-left",
        ) || "0",
      ),
      right: parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--safe-area-inset-right",
        ) || "0",
      ),
    };

    setViewportInfo((prev) => {
      const hasChanged =
        prev.width !== width ||
        prev.height !== height ||
        prev.orientation !== orientation ||
        prev.viewport !== newViewport;

      if (hasChanged) {
        // Trigger haptic feedback for significant viewport changes
        if (prev.viewport !== newViewport) {
          console.log(`🔄 [RAF] Viewport changed: ${prev.viewport} → ${newViewport} (${width}px)`);
          hapticFeedbackService.trigger("navigation");
        }

        return {
          width,
          height,
          orientation,
          devicePixelRatio: window.devicePixelRatio || 1,
          viewport: newViewport,
          isTouch: "ontouchstart" in window || navigator.maxTouchPoints > 0,
          hasHover: window.matchMedia("(hover: hover)").matches,
          hasKeyboard: !("ontouchstart" in window),
          safeArea,
        };
      }

      return prev;
    });
  }, [breakpoints]);

  // Debounced orientation change handler
  const handleOrientationChange = useCallback(() => {
    if (orientationTimeout.current) {
      clearTimeout(orientationTimeout.current);
    }

    orientationTimeout.current = setTimeout(() => {
      updateViewportInfo();
    }, 100); // Debounce orientation changes
  }, [updateViewportInfo]);

  // Initialize viewport monitoring
  useEffect(() => {
    updateViewportInfo();

    // Listen for resize events
    window.addEventListener("resize", updateViewportInfo);
    window.addEventListener("orientationchange", handleOrientationChange);

    // Use ResizeObserver for more precise viewport tracking
    if ("ResizeObserver" in window) {
      resizeObserver.current = new ResizeObserver(() => {
        updateViewportInfo();
      });
      resizeObserver.current.observe(document.documentElement);
    }

    return () => {
      window.removeEventListener("resize", updateViewportInfo);
      window.removeEventListener("orientationchange", handleOrientationChange);

      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
      }

      if (orientationTimeout.current) {
        clearTimeout(orientationTimeout.current);
      }
    };
  }, [updateViewportInfo, handleOrientationChange]);

  // Component adaptation logic
  const adaptComponent = useCallback(
    (component: Component): Component => {
      if (!enableAdvancedAdaptation) return component;

      const { viewport } = viewportInfo;
      const rules = adaptationRules;
      let adaptedComponent = { ...component };

      // Apply viewport-specific adaptations
      switch (viewport) {
        case Viewport.MOBILE:
          adaptedComponent = adaptForMobile(adaptedComponent, rules);
          break;
        case Viewport.TABLET:
          adaptedComponent = adaptForTablet(adaptedComponent, rules);
          break;
        case Viewport.DESKTOP:
          adaptedComponent = adaptForDesktop(adaptedComponent, rules);
          break;
      }

      // Apply performance optimizations if needed
      if (performanceMode) {
        adaptedComponent = applyPerformanceOptimizations(adaptedComponent);
      }

      // Apply battery saver optimizations
      if (batterySaverMode) {
        adaptedComponent = applyBatterySaverOptimizations(adaptedComponent);
      }

      return adaptedComponent;
    },
    [
      viewportInfo,
      adaptationRules,
      enableAdvancedAdaptation,
      performanceMode,
      batterySaverMode,
    ],
  );

  // Mobile-specific adaptations
  const adaptForMobile = useCallback(
    (component: Component, rules: AdaptationRules): Component => {
      const adapted = {
        ...component,
        properties: { ...component.properties },
        size: { ...component.size },
      };

      // Ensure minimum touch target size
      adapted.size.width = ensureMinimumDimension(
        adapted.size.width,
        rules.touchTargetMinimum,
      );
      adapted.size.height = ensureMinimumDimension(
        adapted.size.height,
        rules.touchTargetMinimum,
      );

      // Scale font sizes for better readability
      if (typeof adapted.properties.fontSize === 'number') {
        adapted.properties.fontSize = Math.round(
          adapted.properties.fontSize * rules.fontSizeScaling,
        );
      }

      // Increase spacing for touch interactions
      if (typeof adapted.properties.padding === 'number') {
        adapted.properties.padding = Math.round(
          adapted.properties.padding * rules.spacingScaling,
        );
      }
      if (typeof adapted.properties.margin === 'number') {
        adapted.properties.margin = Math.round(
          adapted.properties.margin * rules.spacingScaling,
        );
      }

      // Enable mobile-specific features
      adapted.properties.touchOptimized = true;
      adapted.properties.enableHapticFeedback = true;
      adapted.properties.enableLongPress = true;

      // Adjust button properties
      if (component.type === "button") {
        adapted.size.height = ensureMinimumDimension(
          adapted.size.height,
          rules.buttonHeightMinimum,
        );
        adapted.properties.variant = adapted.properties.variant || "primary";
      }

      // Optimize text components for mobile
      if (component.type === "text") {
        adapted.properties.lineHeight = adapted.properties.lineHeight || 1.5;
        adapted.properties.textWrap = rules.enableTextWrapping;
      }

      return adapted;
    },
    [],
  );

  // Tablet-specific adaptations
  const adaptForTablet = useCallback(
    (component: Component, rules: AdaptationRules): Component => {
      const adapted = {
        ...component,
        properties: { ...component.properties },
        size: { ...component.size },
      };

      // Moderate scaling for tablet
      const tabletScaling = 0.8; // Less aggressive than mobile

      // Apply scaled touch targets
      const minSize = rules.touchTargetMinimum * tabletScaling;
      adapted.size.width = ensureMinimumDimension(adapted.size.width, minSize);
      adapted.size.height = ensureMinimumDimension(adapted.size.height, minSize);

      // Moderate font scaling
      if (typeof adapted.properties.fontSize === 'number') {
        adapted.properties.fontSize = Math.round(
          adapted.properties.fontSize *
            (1 + (rules.fontSizeScaling - 1) * tabletScaling),
        );
      }

      // Enable touch optimizations but less aggressive
      adapted.properties.touchOptimized = true;
      adapted.properties.enableHapticFeedback = true;

      return adapted;
    },
    [],
  );

  // Desktop adaptations (minimal changes)
  const adaptForDesktop = useCallback(
    (component: Component, rules: AdaptationRules): Component => {
      const adapted = {
        ...component,
        properties: { ...component.properties },
        size: { ...component.size },
      };

      // Desktop-specific optimizations
      adapted.properties.touchOptimized = false;
      adapted.properties.enableHapticFeedback = false;
      adapted.properties.enableHover = true;

      return adapted;
    },
    [],
  );

  // Performance mode adaptations
  const applyPerformanceOptimizations = useCallback(
    (component: Component): Component => {
      const optimized = {
        ...component,
        properties: { ...component.properties },
        size: { ...component.size },
      };

      // Reduce visual complexity
      optimized.properties.disableAnimations = true;
      optimized.properties.reducedShadows = true;
      optimized.properties.enableHardwareAcceleration = false;

      // Simplify interactions
      optimized.properties.enableHapticFeedback = false;
      optimized.properties.disableHover = true;

      return optimized;
    },
    [],
  );

  // Battery saver mode adaptations
  const applyBatterySaverOptimizations = useCallback(
    (component: Component): Component => {
      const optimized = {
        ...component,
        properties: { ...component.properties },
        size: { ...component.size },
      };

      // Aggressive performance optimizations
      optimized.properties.disableAnimations = true;
      optimized.properties.reducedShadows = true;
      optimized.properties.enableHapticFeedback = false;
      optimized.properties.disableAutoRefresh = true;
      optimized.properties.useSystemColors = true;

      return optimized;
    },
    [],
  );

  // Responsive property getter
  const getResponsiveProperty = useCallback(
    <T extends any>(mobile: T, tablet?: T, desktop?: T, ultrawide?: T): T => {
      const { viewport } = viewportInfo;

      switch (viewport) {
        case Viewport.MOBILE:
          return mobile;
        case Viewport.TABLET:
          return tablet !== undefined ? tablet : mobile;
        case Viewport.DESKTOP:
          return desktop !== undefined
            ? desktop
            : tablet !== undefined
              ? tablet
              : mobile;
        default:
          return ultrawide !== undefined
            ? ultrawide
            : desktop !== undefined
              ? desktop
              : tablet !== undefined
                ? tablet
                : mobile;
      }
    },
    [viewportInfo.viewport],
  );

  // Update adaptation rules
  const updateAdaptationRules = useCallback(
    (newRules: Partial<AdaptationRules>) => {
      setAdaptationRules((prev) => ({ ...prev, ...newRules }));
    },
    [],
  );

  // CSS custom properties for responsive design
  useEffect(() => {
    const root = document.documentElement;
    const { viewport, width, height, safeArea } = viewportInfo;

    // Set viewport information as CSS variables
    root.style.setProperty("--viewport-width", `${width}px`);
    root.style.setProperty("--viewport-height", `${height}px`);
    root.style.setProperty("--safe-area-top", `${safeArea.top}px`);
    root.style.setProperty("--safe-area-bottom", `${safeArea.bottom}px`);
    root.style.setProperty("--safe-area-left", `${safeArea.left}px`);
    root.style.setProperty("--safe-area-right", `${safeArea.right}px`);

    // Set adaptation rules as CSS variables
    root.style.setProperty(
      "--touch-target-minimum",
      `${adaptationRules.touchTargetMinimum}px`,
    );
    root.style.setProperty(
      "--font-scale",
      adaptationRules.fontSizeScaling.toString(),
    );
    root.style.setProperty(
      "--spacing-scale",
      adaptationRules.spacingScaling.toString(),
    );
    root.style.setProperty(
      "--button-height-minimum",
      `${adaptationRules.buttonHeightMinimum}px`,
    );

    // Add viewport class to body
    document.body.className = document.body.className.replace(
      /viewport-\w+/g,
      "",
    );
    document.body.classList.add(`viewport-${viewport.toLowerCase()}`);

    if (viewportInfo.isTouch) {
      document.body.classList.add("touch-device");
    } else {
      document.body.classList.remove("touch-device");
    }

    if (performanceMode) {
      document.body.classList.add("performance-mode");
    } else {
      document.body.classList.remove("performance-mode");
    }

    if (batterySaverMode) {
      document.body.classList.add("battery-saver");
    } else {
      document.body.classList.remove("battery-saver");
    }
  }, [viewportInfo, adaptationRules, performanceMode, batterySaverMode]);

  const contextValue: ResponsiveAdaptationContextValue = {
    viewportInfo,
    adaptationRules,
    breakpoints,
    adaptComponent,
    getResponsiveProperty,
    updateAdaptationRules,
    performanceMode,
    batterySaverMode,
  };

  return (
    <ResponsiveAdaptationContext.Provider value={contextValue}>
      {children}
    </ResponsiveAdaptationContext.Provider>
  );
};

// Hook to use the responsive adaptation context
export const useResponsiveAdaptation = () => {
  const context = useContext(ResponsiveAdaptationContext);
  return context ?? createFallbackResponsiveAdaptationContext();
};

function createFallbackResponsiveAdaptationContext(): ResponsiveAdaptationContextValue {
  const width = typeof window === "undefined" ? 1200 : window.innerWidth;
  const height = typeof window === "undefined" ? 800 : window.innerHeight;
  const viewport =
    width <= fallbackBreakpoints.mobile
      ? Viewport.MOBILE
      : width <= fallbackBreakpoints.tablet
        ? Viewport.TABLET
        : Viewport.DESKTOP;

  return {
    viewportInfo: {
      width,
      height,
      orientation: width > height ? "landscape" : "portrait",
      devicePixelRatio:
        typeof window === "undefined" ? 1 : window.devicePixelRatio || 1,
      viewport,
      isTouch:
        typeof window !== "undefined" &&
        ("ontouchstart" in window || navigator.maxTouchPoints > 0),
      hasHover:
        typeof window !== "undefined" &&
        window.matchMedia("(hover: hover)").matches,
      hasKeyboard:
        typeof window === "undefined" || !("ontouchstart" in window),
      safeArea: { top: 0, bottom: 0, left: 0, right: 0 },
    },
    adaptationRules: fallbackAdaptationRules,
    breakpoints: fallbackBreakpoints,
    adaptComponent: (component) => component,
    getResponsiveProperty: (mobile, tablet, desktop, ultrawide) => {
      switch (viewport) {
        case Viewport.MOBILE:
          return mobile;
        case Viewport.TABLET:
          return tablet ?? mobile;
        case Viewport.DESKTOP:
        default:
          return desktop ?? tablet ?? ultrawide ?? mobile;
      }
    },
    updateAdaptationRules: () => {},
    performanceMode: false,
    batterySaverMode: false,
  };
}

// Utility hooks for common responsive patterns
export const useViewportInfo = () => {
  const { viewportInfo } = useResponsiveAdaptation();
  return viewportInfo;
};

export const useResponsiveValue = <T,>(
  mobile: T,
  tablet?: T,
  desktop?: T,
  ultrawide?: T,
) => {
  const { getResponsiveProperty } = useResponsiveAdaptation();
  return getResponsiveProperty(mobile, tablet, desktop, ultrawide);
};

export const useIsMobile = () => {
  const { viewportInfo } = useResponsiveAdaptation();
  return viewportInfo.viewport === Viewport.MOBILE;
};

export const useIsTablet = () => {
  const { viewportInfo } = useResponsiveAdaptation();
  return viewportInfo.viewport === Viewport.TABLET;
};

export const useIsDesktop = () => {
  const { viewportInfo } = useResponsiveAdaptation();
  return viewportInfo.viewport === Viewport.DESKTOP;
};

export const useTouchDevice = () => {
  const { viewportInfo } = useResponsiveAdaptation();
  return viewportInfo.isTouch;
};

export const useAdaptedComponent = (component: Component) => {
  const { adaptComponent } = useResponsiveAdaptation();
  return useMemo(() => adaptComponent(component), [adaptComponent, component]);
};

export default ResponsiveAdaptationFramework;
